import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { toast } from 'sonner';
import { X, Send, Upload, XCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL.endsWith('/api') ? BACKEND_URL : `${BACKEND_URL}/api`;

const AddCandidatureModal = ({ isOpen, onClose, onSuccess, jobOfferId }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    prenom: '',
    nom: '',
    email: '',
    telephone: '',
    commentaire: '',
    ajouter_au_vivier: false
  });
  const [files, setFiles] = useState({
    cv: null,
    lettre: null
  });
  const [uploadedFiles, setUploadedFiles] = useState({
    cv_filename: null,
    lettre_filename: null
  });

  if (!isOpen) return null;

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFileChange = (type, file) => {
    if (!file) {
      setFiles(prev => ({
        ...prev,
        [type]: null
      }));
      return;
    }

    // Validation du format de fichier
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ];
    
    if (!allowedTypes.includes(file.type)) {
      toast.error('Format de fichier non autorisé. Seuls les fichiers PDF, DOC et DOCX sont acceptés.');
      return;
    }

    // Validation de la taille (10 Mo max)
    const maxSize = 10 * 1024 * 1024; // 10 MB
    if (file.size > maxSize) {
      toast.error('Le fichier est trop volumineux. Taille maximum autorisée : 10 Mo.');
      return;
    }

    setFiles(prev => ({
      ...prev,
      [type]: file
    }));
  };

  const uploadFile = async (file) => {
    const formData = new FormData();
    formData.append('file', file);

    const token = localStorage.getItem('token');
    const response = await fetch(`${API}/upload`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData
    });

    if (response.ok) {
      const result = await response.json();
      return result.filename;
    } else {
      throw new Error('Erreur lors du téléchargement du fichier');
    }
  };

  const handleSubmit = async () => {
    // Validation
    if (!formData.prenom.trim()) {
      toast.error('Le prénom est requis');
      return;
    }
    if (!formData.nom.trim()) {
      toast.error('Le nom est requis');
      return;
    }

    setLoading(true);

    try {
      // Upload files if provided
      let cv_filename = null;
      let lettre_filename = null;

      if (files.cv) {
        cv_filename = await uploadFile(files.cv);
      }
      if (files.lettre) {
        lettre_filename = await uploadFile(files.lettre);
      }

      // Create candidature
      const token = localStorage.getItem('token');
      const payload = {
        job_offer_id: jobOfferId,
        prenom: formData.prenom.trim(),
        nom: formData.nom.trim(),
        email: formData.email.trim() || null,
        telephone: formData.telephone.trim() || null,
        commentaire: formData.commentaire.trim() || null,
        cv_filename,
        lettre_filename,
        ajouter_au_vivier: formData.ajouter_au_vivier
      };

      const response = await fetch(`${API}/candidatures`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        toast.success('Candidature ajoutée avec succès');
        onSuccess();
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || 'Erreur lors de l\'ajout de la candidature');
      }
    } catch (error) {
      console.error('Error adding candidature:', error);
      toast.error('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    // Reset form
    setFormData({
      prenom: '',
      nom: '',
      email: '',
      telephone: '',
      commentaire: '',
      ajouter_au_vivier: false
    });
    setFiles({
      cv: null,
      lettre: null
    });
    setUploadedFiles({
      cv_filename: null,
      lettre_filename: null
    });
    onClose();
  };

  return (
    <div className="add-candidature-modal-overlay">
      <div className="add-candidature-modal">
        <Card className="add-candidature-modal-card">
          <CardHeader className="add-candidature-modal-header">
            <div className="flex justify-between items-center">
              <CardTitle className="add-candidature-modal-title">
                Ajouter une candidature
              </CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleCancel}
                className="add-candidature-modal-close"
              >
                <X size={20} />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="add-candidature-modal-content">
            <form className="add-candidature-form">
              {/* Prénom et Nom */}
              <div className="form-row">
                <div className="form-group">
                  <Label htmlFor="prenom" className="form-label">
                    Prénom *
                  </Label>
                  <Input
                    id="prenom"
                    type="text"
                    value={formData.prenom}
                    onChange={(e) => handleInputChange('prenom', e.target.value)}
                    className="form-input"
                    placeholder=""
                  />
                </div>
                <div className="form-group">
                  <Label htmlFor="nom" className="form-label">
                    Nom *
                  </Label>
                  <Input
                    id="nom"
                    type="text"
                    value={formData.nom}
                    onChange={(e) => handleInputChange('nom', e.target.value)}
                    className="form-input"
                    placeholder=""
                  />
                </div>
              </div>

              {/* Email et Téléphone */}
              <div className="form-row">
                <div className="form-group">
                  <Label htmlFor="email" className="form-label">
                    Email
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className="form-input"
                    placeholder=""
                  />
                </div>
                <div className="form-group">
                  <Label htmlFor="telephone" className="form-label">
                    Téléphone
                  </Label>
                  <Input
                    id="telephone"
                    type="tel"
                    value={formData.telephone}
                    onChange={(e) => handleInputChange('telephone', e.target.value)}
                    className="form-input"
                    placeholder=""
                  />
                </div>
              </div>

              {/* CV Upload */}
              <div className="form-group">
                <Label htmlFor="cv" className="form-label">
                  CV
                </Label>
                <div className="file-upload-container">
                  <Input
                    id="cv"
                    type="file"
                    accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    onChange={(e) => handleFileChange('cv', e.target.files[0])}
                    className="form-input file-input"
                  />
                  {files.cv && (
                    <div className="file-preview">
                      <Upload size={16} />
                      <span>{files.cv.name}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Lettre de motivation Upload */}
              <div className="form-group">
                <Label htmlFor="lettre" className="form-label">
                  Lettre de motivation
                </Label>
                <div className="file-upload-container">
                  <Input
                    id="lettre"
                    type="file"
                    accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    onChange={(e) => handleFileChange('lettre', e.target.files[0])}
                    className="form-input file-input"
                  />
                  {files.lettre && (
                    <div className="file-preview">
                      <Upload size={16} />
                      <span>{files.lettre.name}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Commentaire */}
              <div className="form-group">
                <Label htmlFor="commentaire" className="form-label">
                  Commentaire
                </Label>
                <Textarea
                  id="commentaire"
                  value={formData.commentaire}
                  onChange={(e) => handleInputChange('commentaire', e.target.value)}
                  className="form-input"
                  rows={3}
                  placeholder=""
                />
              </div>

              {/* Case à cocher vivier */}
              <div className="form-group">
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="vivier"
                    checked={formData.ajouter_au_vivier}
                    onChange={(e) => handleInputChange('ajouter_au_vivier', e.target.checked)}
                    className="h-4 w-4 rounded border border-gray-300 focus:ring-2 focus:ring-primary"
                  />
                  <Label htmlFor="vivier" className="form-label-checkbox cursor-pointer">
                    Ajouter au vivier des candidatures
                  </Label>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="add-candidature-modal-actions">
                <Button
                  type="button"
                  onClick={handleSubmit}
                  disabled={loading}
                  className="btn-primary"
                  style={{ backgroundColor: '#eb5d2d', borderColor: '#eb5d2d' }}
                >
                  <Send size={16} className="mr-2" />
                  {loading ? 'Envoi...' : 'Envoyer'}
                </Button>
                <Button
                  type="button"
                  onClick={handleCancel}
                  disabled={loading}
                  variant="outline"
                  className="btn-cancel"
                >
                  <XCircle size={16} className="mr-2" />
                  Annuler
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AddCandidatureModal;